import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { trackV2 } from 'utils/analytics';
import { logError } from 'utils/sentry';
import { devLog } from 'utils/devUtils';
import styles from './styles.scss';

const GOOGLE_APP_LINK =
  'https://play.google.com/store/apps/details?id=com.strava';
const APPLE_APP_LINK =
  'https://itunes.apple.com/app/strava-cycling/id426826309?mt=8';

const trackClickEvent = (props) => {
  trackV2({
    ...props,
    action: 'click'
  });
};

const FooterUpsell = ({ authUrls, analyticsData, deeplink }) => {
  const hardcodedAppLink = (() => {
    const { userAgent } = window.navigator;
    if (userAgent.includes('Android')) {
      return GOOGLE_APP_LINK;
    }
    if (userAgent.includes('iPad') || userAgent.includes('iPhone')) {
      return APPLE_APP_LINK;
    }
    return '/mobile';
  })();

  const [appLink, setAppLink] = useState(hardcodedAppLink);

  const linkData = {
    channel: 'web',
    feature: `${analyticsData.page} footer upsell`,
    data: {
      strava_deeplink_url: deeplink,
      $desktop_url: `${window.location.origin}/register`,
      logged_out_deeplink: true // https://github.com/strava/android/pull/20088
    }
  };

  const createAppLink = useCallback(() => {
    // uses `window.branch.link` directly instead of `window.Strava.BranchIO` bc
    // the `window.Strava.BranchIO` object is not enabled after cookies have been
    // accepted until the page has been reloaded
    window.branch.link(linkData, (err, link) => {
      if (err) {
        logError(err, {
          method: 'legacy FooterUpsell window.branch.link',
          linkData: JSON.stringify(linkData),
          ...(linkData?.tags && {
            'linkData.tags': JSON.stringify(linkData?.tags)
          })
        });
      }
      devLog(
        `FooterUpsell applink - ${link} ::: linkData: ${JSON.stringify(
          linkData
        )}`
      );
      setAppLink(link);
    });
  }, [linkData]);

  useEffect(() => {
    if (window.branch) {
      createAppLink();
    } else {
      window.addEventListener('BranchInitialized', createAppLink);
    }

    return () => {
      window.removeEventListener('BranchInitialized', createAppLink);
    };
  }, [createAppLink]);

  return (
    <div className={styles.container}>
      <h3>{I18n.t('strava.athletes.show_public.bottom_cta_header')}</h3>

      <div className={styles.mobile}>
        <a
          href={appLink}
          className={`btn btn-primary footer-download-btn ${styles.btn}`}
          onClick={() =>
            trackClickEvent({
              ...analyticsData,
              element: 'get_app',
              properties: { cta_copy: 'get_the_app', url: appLink }
            })
          }
        >
          {I18n.t('strava.athletes.show_public.get_the_app_cta')}
        </a>
      </div>

      <div className={styles.desktop}>
        <a
          href={authUrls.facebook}
          className={`btn btn-facebook ${styles.btnFacebook} ${styles.btn}`}
          onClick={() =>
            trackClickEvent({ ...analyticsData, element: 'facebook_signup' })
          }
        >
          {I18n.t('strava.athletes.show_public.facebook_cta')}
        </a>

        <a
          href={authUrls.google}
          className={`btn btn-default ${styles.btnGoogle} ${styles.btn}`}
          onClick={() =>
            trackClickEvent({ ...analyticsData, element: 'google_signup' })
          }
        >
          {I18n.t('strava.athletes.show_public.google_cta')}
        </a>

        <a
          href={authUrls.email}
          className={`btn btn-primary ${styles.btnEmail} ${styles.btn}`}
          onClick={() =>
            trackClickEvent({
              ...analyticsData,
              element: 'start_signup',
              properties: { signup_copy: 'signup_using_email' }
            })
          }
        >
          {I18n.t('strava.athletes.show_public.email_cta')}
        </a>
      </div>
    </div>
  );
};

FooterUpsell.propTypes = {
  authUrls: PropTypes.shape({
    facebook: PropTypes.string.isRequired,
    google: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }).isRequired,
  analyticsData: PropTypes.shape().isRequired,
  deeplink: PropTypes.string.isRequired
};

export default FooterUpsell;
